import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';

interface ProjectFormState {
  isMinimized: boolean;
  formData: any;
  hasUnsavedChanges: boolean;
  shouldOpenModal: boolean;
}

interface ProjectFormContextType {
  formState: ProjectFormState | null;
  minimizeForm: (data: any) => void;
  restoreForm: () => void;
  clearForm: () => void;
  hasMinimizedForm: boolean;
  acknowledgeModalOpen: () => void;
}

const ProjectFormContext = createContext<ProjectFormContextType | undefined>(undefined);

export function ProjectFormProvider({ children }: { children: ReactNode }) {
  const [formState, setFormState] = useState<ProjectFormState | null>(null);

  const minimizeForm = (data: any) => {
    setFormState({
      isMinimized: true,
      formData: data,
      hasUnsavedChanges: true,
      shouldOpenModal: false,
    });
  };

  const restoreForm = () => {
    if (formState) {
      setFormState({
        ...formState,
        isMinimized: false,
        shouldOpenModal: true,
      });
    }
  };

  const clearForm = () => {
    setFormState(null);
  };

  const acknowledgeModalOpen = () => {
    if (formState) {
      setFormState({
        ...formState,
        shouldOpenModal: false,
      });
    }
  };

  const hasMinimizedForm = formState?.isMinimized === true;

  return (
    <ProjectFormContext.Provider
      value={{
        formState,
        minimizeForm,
        restoreForm,
        clearForm,
        hasMinimizedForm,
        acknowledgeModalOpen,
      }}
    >
      {children}
    </ProjectFormContext.Provider>
  );
}

export function useProjectForm() {
  const context = useContext(ProjectFormContext);
  if (context === undefined) {
    throw new Error('useProjectForm must be used within a ProjectFormProvider');
  }
  return context;
}
